import { normalizeLocationText } from "../location/locationSearch.ts";
import type {
  CanonicalCondition,
  CanonicalMarketPropertyType,
  MarketAreaBasis,
  MarketAreaModel,
  MarketPropertySnapshot
} from "./types.ts";

export const normalizeSlovakLocationName = (value?: string) => normalizeLocationText(value ?? "")
  .replace(/\b(?:ulica|ul)\b[.\s]*/g, "")
  .replace(/\bnam\b/g, "namestie")
  .replace(/\bsidl\b/g, "sidlisko")
  .replace(/[.,/\\()[\]{}:;]+/g, " ")
  .replace(/\s+/g, " ")
  .trim();

export const canonicalPropertyType = (value?: string): CanonicalMarketPropertyType => {
  const normalized = normalizeSlovakLocationName(value);
  if (/\b(?:byt|byty|apartman|apartmany|apartment)\b/.test(normalized) || /\bizbovy byt\b/.test(normalized)) return "apartment";
  if (/\b(?:rodinny dom|rodinne domy|dom|domy|vila|vily)\b/.test(normalized)) return "house";
  if (/\b(?:pozemok|pozemky|parcela|parcely)\b/.test(normalized)) return "land";
  if (/\b(?:chata|chaty|chalupa|chalupy|rekreac)/.test(normalized)) return "recreational";
  if (/\b(?:komerc|administrativ|kancelar|obchodny priestor)\b/.test(normalized)) return "commercial";
  if (/\bgaraz(?:e)?\b/.test(normalized)) return "garage";
  if (/\b(?:developersky projekt|developerske projekty|development)\b/.test(normalized)) return "development";
  return "other";
};

export const canonicalCondition = (value?: string): CanonicalCondition => {
  const normalized = normalizeSlovakLocationName(value);
  if (normalized === "original"
    || normalized === "partial_reconstruction"
    || normalized === "full_reconstruction"
    || normalized === "new_build"
    || normalized === "shell"
    || normalized === "unknown") return normalized;
  if (!normalized || normalized.includes("nezist") || normalized.includes("neuved")) return "unknown";
  if (normalized.includes("povod") || normalized.includes("havar")) return "original";
  if (normalized.includes("ciast") || normalized.includes("moderniz")) return "partial_reconstruction";
  if (normalized.includes("komplet") || normalized === "rekonstrukcia") return "full_reconstruction";
  if (normalized.includes("novost") || normalized.includes("novy")) return "new_build";
  if (normalized.includes("holob") || normalized.includes("shell")) return "shell";
  return "unknown";
};

const conditionQualityRank = (value?: string) => {
  const condition = canonicalCondition(value);
  return condition === "original" ? 0
    : condition === "partial_reconstruction" ? 1
      : condition === "full_reconstruction" ? 2
        : condition === "new_build" ? 3
          : undefined;
};

export const isCheaperBetterConditionAlternative = (
  subject: Pick<MarketPropertySnapshot, "condition" | "technical"> & { askingPriceAtAnalysis?: number },
  candidate: Pick<MarketPropertySnapshot, "condition" | "technical"> & { askingPrice?: number }
) => {
  if (!subject.askingPriceAtAnalysis || !candidate.askingPrice || candidate.askingPrice >= subject.askingPriceAtAnalysis) return false;
  const subjectRank = conditionQualityRank(subject.technical?.condition ?? subject.condition);
  const candidateRank = conditionQualityRank(candidate.technical?.condition ?? candidate.condition);
  return subjectRank !== undefined && candidateRank !== undefined && candidateRank > subjectRank;
};

export const municipalityName = (property: MarketPropertySnapshot) =>
  property.location?.municipality || property.city;

export const municipalityId = (property: MarketPropertySnapshot) =>
  property.location?.municipalityId || property.municipalityId;

export const sameMunicipality = (first: MarketPropertySnapshot, second: MarketPropertySnapshot) => {
  const firstId = municipalityId(first);
  const secondId = municipalityId(second);
  if (firstId && secondId) return firstId === secondId;
  const firstName = normalizeSlovakLocationName(municipalityName(first));
  const secondName = normalizeSlovakLocationName(municipalityName(second));
  return Boolean(firstName && secondName && firstName === secondName);
};

export type EffectiveArea = {
  value?: number;
  basis: MarketAreaBasis;
  confidence: "high" | "medium" | "low";
};

export const effectiveApartmentArea = (property: Pick<MarketPropertySnapshot, "area" | "areas">): EffectiveArea => {
  const areas = property.areas;
  if (areas?.apartmentArea) {
    return areas.areaBasis === "legacy_unspecified"
      ? { value: areas.apartmentArea, basis: "legacy_unspecified", confidence: "medium" }
      : { value: areas.apartmentArea, basis: "apartment", confidence: "high" };
  }
  if (areas?.interiorArea) return { value: areas.interiorArea, basis: "interior", confidence: "high" };
  if (areas?.totalArea) {
    return areas.areaBasis === "legacy_unspecified"
      ? { value: areas.totalArea, basis: "legacy_unspecified", confidence: "low" }
      : { value: areas.totalArea, basis: "total_only", confidence: "low" };
  }
  return { value: property.area, basis: "legacy_unspecified", confidence: property.area ? "medium" : "low" };
};

export const normalizedAreaModel = (input: Partial<MarketAreaModel>, legacyArea?: number): MarketAreaModel => {
  if (input.apartmentArea) return { ...input, areaBasis: "apartment" };
  if (input.interiorArea) return { ...input, areaBasis: "interior" };
  if (input.totalArea) return { ...input, areaBasis: "total_only" };
  return { ...input, apartmentArea: undefined, interiorArea: undefined, totalArea: legacyArea, areaBasis: "legacy_unspecified" };
};

export const isPlausibleApartmentArea = (area?: number) => Boolean(area && Number.isFinite(area) && area >= 10 && area <= 500);
export const isPlausibleMarketPrice = (price?: number) => Boolean(price && Number.isFinite(price) && price >= 1_000 && price <= 100_000_000);
export const isPlausiblePricePerM2 = (price?: number, area?: number) => {
  if (!isPlausibleMarketPrice(price) || !isPlausibleApartmentArea(area)) return false;
  const value = price! / area!;
  return value >= 100 && value <= 30_000;
};

export const isActiveAskingListing = (status?: string) => {
  const normalized = normalizeSlovakLocationName(status);
  if (!normalized) return true;
  return !/(?:rezerv|predan|sold|stiahnut|inactive|neaktiv)/.test(normalized);
};
